import * as Yup from "yup";
import replaceDecimalComma from "../functions/replaceDecimalComma.function";
import { onlyDigitsError } from "./YupValidationErrors";

const MESSAGE_NO_NUMERIC_STRING = "The field should have numbers only.";
const MESSAGE_NO_THOUSANDS_SEPARATOR = "Please only use decimal dot or comma but no thousands separator.";

const NO_THOUSANDS_SEPARATOR =
  /^(?!(\d{1,3}\.|\d{1,3},)(\d{3},|\d{3}\.)(\d+)|(\s+)(\d{1,3}\.|\d{1,3},)(\d{3},|\d{3}\.)(\d+))/;

declare module "yup" {
  interface StringSchema {
    isNumeric(message: string): StringSchema;
    numericStringMax(comparator: string, message: string): StringSchema;
    numericStringMin(comparator: string, message: string): StringSchema;
    numericStringNoThousandsSeparator(message: string): StringSchema;
  }
}

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  "isNumeric",
  // eslint-disable-next-line func-names
  function (message: string) {
    return this.test("isNumeric", message, (value: string) => {
      const asNumber = Number(replaceDecimalComma(value || ""));
      return !Number.isNaN(asNumber);
    });
  },
);

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  "numericStringMax",
  // eslint-disable-next-line func-names
  function (comparator: string, message: string) {
    return this.isNumeric(MESSAGE_NO_NUMERIC_STRING).test("numericStringMax", message, (value: string) => {
      const comparatorNumber = Number(replaceDecimalComma(comparator || ""));
      const valueNumber = Number(replaceDecimalComma(value || ""));
      return valueNumber < comparatorNumber - 1;
    });
  },
);

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  "numericStringMin",
  // eslint-disable-next-line func-names
  function (comparator: string, message: string) {
    return this.isNumeric(MESSAGE_NO_NUMERIC_STRING).test("numericStringMin", message, (value: string) => {
      const comparatorNumber = Number(replaceDecimalComma(comparator || ""));
      const valueNumber = Number(replaceDecimalComma(value || ""));
      return valueNumber > comparatorNumber + 1;
    });
  },
);

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  "numericStringNoThousandsSeparator",
  // eslint-disable-next-line func-names
  function (message: string) {
    return this.matches(NO_THOUSANDS_SEPARATOR, {
      message,
      excludeEmptyString: true,
    });
  },
);

// eslint-disable-next-line import/prefer-default-export
export const noSeparatorDecimalCommaTransformed = Yup.string()
  .numericStringNoThousandsSeparator(MESSAGE_NO_THOUSANDS_SEPARATOR)
  .isNumeric(MESSAGE_NO_NUMERIC_STRING);

export const validPositiveInteger = () => {
  return Yup.number()
    .typeError(onlyDigitsError)
    .min(0, "Value must be greater or equal to 0.")
    .max(2_000_000_000, "Value cannot exceed 2 billion.");
};

export const validPositiveBigDecimal = () => {
  return Yup.number()
    .test("is-decimal", onlyDigitsError, (value) => {
      return !value || Boolean(`${value}`.match(/^\d*(\.\d{1,4})?$/));
    })
    .min(0, "Value must be greater or equal to 0.")
    .max(1_000_000_000_000, "Value cannot exceed 1 trillion.");
};
